import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import { formatCurrency } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ContractorPortalPageShell from "../components/ContractorPortalPageShell";
import {
  ContractPdfViewerDialog,
  ContractorContractPageLayout,
} from "../components/contract-ux";
import { CONTRACT_UX } from "../components/contract-ux/contractUxTheme";
import ContractorStatusBadge from "../components/ContractorStatusBadge";
import { CONTRACTOR_PORTAL_BASE } from "../contractorPortalNav";
import { downloadPdfBlob } from "../utils/contractPdfActions";
import { resolveContractorContractUx } from "../utils/contractorContractUx";

const annexFileUrl = (annex) => annex?.signedPdfUrl || annex?.finalPdfUrl || null;

const ContractorContractsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [master, setMaster] = useState(null);
  const [annexes, setAnnexes] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    setError(null);
    const res = await ContractorPortalApi.getContracts();
    if (!res?.isSuccess) {
      setError(res?.message || "خطا در بارگذاری قراردادها.");
      setMaster(null);
      setAnnexes([]);
    } else {
      setMaster(res.data?.master || null);
      setAnnexes(res.data?.annexes || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await ContractorPortalApi.getContracts();
      if (cancelled) return;
      if (!res?.isSuccess) {
        setError(res?.message || "خطا در بارگذاری قراردادها.");
        setMaster(null);
        setAnnexes([]);
      } else {
        setMaster(res.data?.master || null);
        setAnnexes(res.data?.annexes || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ux = useMemo(
    () =>
      resolveContractorContractUx(master, {
        contractsPath: `${CONTRACTOR_PORTAL_BASE}/contracts`,
        projectsPath: `${CONTRACTOR_PORTAL_BASE}/projects`,
      }),
    [master]
  );

  const handleDownload = async () => {
    if (!master?.contractId) {
      toast.info("فایل قرارداد هنوز آماده نیست.");
      return;
    }
    setPdfBusy(true);
    const res = await ContractorPortalApi.downloadMasterContractPdf(
      master.contractId,
      { preferSigned: true }
    );
    setPdfBusy(false);
    if (!res?.isSuccess || !res.data) {
      toast.error(res?.message || "دانلود قرارداد ناموفق بود.");
      return;
    }
    downloadPdfBlob(
      res.data,
      `contract-${master.contractNumber || master.contractId}.pdf`
    );
  };

  const handleView = () => {
    if (!master?.contractId || !ux.canDownload) {
      toast.info("فایل قرارداد هنوز آماده نیست.");
      return;
    }
    setViewerOpen(true);
  };

  const handleActionClick = (action) => {
    if (action?.scrollTo === "guarantee") {
      setTimeout(() => {
        document
          .getElementById("guarantee-upload-section")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  const isEmpty = !master && !annexes.length;

  return (
    <Stack spacing={1.5} dir="rtl">
      <BreadcrumbsComponent />
      <ContractorPortalPageShell
        title="قراردادها"
        compact
        loading={loading || pdfBusy}
        error={error}
      >
        {isEmpty ? (
          <Alert
            severity="info"
            data-testid="contractor-contracts-empty"
            sx={{ py: 1 }}
          >
            هنوز قراردادی برای شما صادر نشده است.
          </Alert>
        ) : (
          <Stack
            spacing={CONTRACT_UX.sectionGap}
            data-testid="contractor-contracts-panel"
            dir="rtl"
          >
            {master ? (
              <ContractorContractPageLayout
                ux={ux}
                contract={master}
                onDownload={handleDownload}
                onView={handleView}
                onActionClick={handleActionClick}
                onGuaranteeUploaded={reload}
              />
            ) : (
              <Alert severity="info" sx={{ py: 1 }}>
                اطلاعات پس از تکمیل فرآیند نمایش داده می‌شود.
              </Alert>
            )}

            {annexes.length ? (
              <Box
                data-testid="contractor-annexes-card"
                sx={{
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: `1px solid ${CONTRACT_UX.cardBorder}`,
                  px: CONTRACT_UX.cardPadding,
                  py: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                  پیوست‌های پروژه
                </Typography>
                <Stack spacing={1.25} divider={<Divider flexItem />}>
                  {annexes.map((annex) => {
                    const url = annexFileUrl(annex);
                    return (
                      <Stack
                        key={annex.annexId || annex.annexNumber}
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                        data-testid={`contractor-annex-row-${annex.annexId}`}
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            {annex.projectCode || "بدون کد پروژه"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            شماره پیوست: {annex.annexNumber || "—"}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mt: 0.5 }}
                          >
                            <ContractorStatusBadge
                              status={annex.status}
                              statusTitle={annex.statusTitle}
                            />
                            <Typography variant="body2">
                              {annex.contractorContractAmount != null
                                ? formatCurrency(
                                    annex.contractorContractAmount,
                                    "rial"
                                  )
                                : "مبلغ: —"}
                            </Typography>
                          </Stack>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!url}
                          href={url || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`contractor-annex-download-${annex.annexId}`}
                          sx={{
                            borderColor: CONTRACT_UX.primary,
                            color: CONTRACT_UX.primary,
                          }}
                        >
                          دانلود
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        )}
      </ContractorPortalPageShell>

      <ContractPdfViewerDialog
        open={viewerOpen}
        contractId={master?.contractId}
        contractNumber={master?.contractNumber}
        onClose={() => setViewerOpen(false)}
      />
    </Stack>
  );
};

export default ContractorContractsPage;
